"use client";
import { Label } from "@/components/atoms/Label";
import { cn } from "@/lib/utils";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  FormProviderProps,
} from "react-hook-form";
import {
  FormFieldContext,
  FormItemContext,
  useFormField,
} from "./form-context";

export type FormProps<TFieldValues extends FieldValues = {}> =
  FormProviderProps<TFieldValues> & {
    className?: string;
    onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
    fullWidth?: boolean;
    spacing?: 0 | 16 | 24 | 32;
    testId?: string;
    noValidate?: boolean;
    id?: string;
  };
const Form = <TFieldValues extends FieldValues>({
  className,
  onSubmit,
  fullWidth,
  children,
  spacing = 24,
  noValidate = true,
  testId,
  id,
  ...formProviderProps
}: FormProps<TFieldValues>) => {
  return (
    <FormProvider {...formProviderProps}>
      <form
        data-testid={testId}
        noValidate={noValidate}
        id={id}
        className={cn(
          {
            "tw-w-full": fullWidth,
            "tw-space-y-16": spacing === 16,
            "tw-space-y-24": spacing === 24,
            "tw-space-y-32": spacing === 32,
          },
          className,
        )}
        onSubmit={onSubmit}
      >
        {children}
      </form>
    </FormProvider>
  );
};

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    disabled?: boolean;
    readOnly?: boolean;
  }
>(({ disabled, readOnly, className, ...props }, ref) => {
  return (
    <FormItemContext.Provider value={{ disabled, readOnly }}>
      <div ref={ref} className={cn("tw-space-y-8", className)} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & {
    subcopy?: React.ReactNode;
    required?: boolean;
  }
>(({ subcopy, className, required, children, ...props }, ref) => {
  const { formItemId, disabled } = useFormField();

  return (
    <div>
      <Label
        ref={ref}
        className={cn({ "tw-text-gray-500": disabled }, className)}
        htmlFor={formItemId}
        {...props}
      >
        {children}
        {required && <span className="tw-text-purple-800 tw-ml-4">*</span>}
      </Label>
      {subcopy && <FormDescription>{subcopy}</FormDescription>}
    </div>
  );
});
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot> & {
    errorMsg?: string;
    disabled?: boolean;
    readOnly?: boolean;
  }
>(({ ...props }, ref) => {
  const {
    error,
    disabled,
    readOnly,
    formItemId,
    formDescriptionId,
    formMessageId,
  } = useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      disabled={disabled}
      errorMsg={error?.message}
      readOnly={readOnly}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
});

FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId, disabled } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn(
        "tw-body-1 tw-text-gray-600",
        {
          "tw-text-gray-500": disabled,
        },
        className,
      )}
      {...props}
    />
  );
});
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId, disabled } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn(
        "tw-caption-2 tw-text-gray-600",
        {
          "tw-text-gray-500": disabled,
          "tw-text-red-800": !!error,
        },
        className,
      )}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessage.displayName = "FormMessage";

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
};
